// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The phone layout, as a model (M3-32, M3-33, 08 §5).
//
// "**The phone is a first-class place to play**, not a fallback." Portrait is the default,
// because pinball is a portrait game and the phone is held that way one-handed.
//
// Everything here is arithmetic over a viewport, and it is separated from the components and
// from the touch listener for one reason: **the same rectangles decide what is drawn and what a
// finger does.** A flipper zone the stylesheet puts in one place and the listener tests for in
// another is a machine where the visible boundary is a lie — and the boundary is only visible on
// a player's very first ball (08 §5.2), so the lie would be invisible from the second ball
// onwards. One source of rectangles, and both sides read it.
//
// 08 §5.1's portrait stack:
//
//     ┌──────────────────────────────┐
//     │  DMD strip                   │   the backbox reduced to what matters
//     ├──────────────────────────────┤
//     │  playfield, Follow           │   ~72% of the screen
//     ├──────────┬──────────┬────────┤
//     │ ◀ LEFT   │  RIGHT ▶ │ ▼ PULL │   ~18%: the cabinet — two flipper
//     └──────────┴──────────┴────────┘   buttons and the plunger's column
//
// 08 §5.3: landscape uses the desktop framing with flipper zones on the left and right thirds.
//
// **The plunger has its own column rather than an arc over the right flipper** (M3-49). It used to
// be a quarter-disc centred on the bottom-right corner, overlapping the right zone, with the
// overlap resolved in favour of whichever control was live. That was survivable only because
// neither control was drawn: on a 390 px phone the arc's radius is 133 px and a flipper button in
// the obvious place — the middle of the right half of the band — is 117 px from that corner, so
// the moment the zones became real buttons the right one would have been a button a player could
// see, press, and watch draw the plunger instead. A control that is visible has to own its
// rectangle.
//
// The column is **permanent**, present whether or not a ball is waiting. A lane that appeared with
// the ball would move the flipper boundary under a thumb that was already resting on it, twice a
// ball.

/** How the phone is being held. */
export type Orientation = 'portrait' | 'landscape'

/** A rectangle in CSS pixels, origin top-left. */
export interface Rect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

/** The portrait stack, as fractions of the viewport height. They sum to one. */
export const DMD_FRACTION = 0.1
export const PLAYFIELD_FRACTION = 0.72
export const FLIPPER_FRACTION = 0.18

/**
 * The tallest the portrait flipper band may be, in CSS pixels (M3-46).
 *
 * **A fraction is the right rule for a phone and the wrong one for a tablet.** 18% of an iPhone is
 130 px — about a thumb — and 18% of an iPad Pro 12.9 in portrait is **236 px**, which is a quarter
 * of a metre of near-empty band taken from the machine. The band exists to be reachable by a thumb
 * at the bottom of the screen, and a thumb does not get longer with the device: past a certain
 * height the extra pixels are not more reachable, they are just less machine.
 *
 * 160 is the fraction's own answer at about 890 px of stage height — a large phone — so nothing
 * any phone shows changes, and every tablet gets the band a phone gets and the rest as playfield.
 * An iPad Pro portrait gains 76 px of machine; an iPad 10.9 gains 31.
 */
export const FLIPPER_BAND_MAX_PX = 160

/** How tall the portrait band actually is on a viewport of this height. */
export function flipperBandHeight(height: number): number {
  return Math.min(Math.round(height * FLIPPER_FRACTION), FLIPPER_BAND_MAX_PX)
}

/**
 * The stage's grid rows, in the same numbers the touch layout is divided by (M3-46, M11-62).
 *
 * **Pixels once the stage has been measured, and that is what M3-46 changed.** The rows were three
 * fractions, which is the same arithmetic `phoneLayout` does — until the band gained a ceiling for
 * tablets. A fraction here and a capped pixel height there would put the drawn boundary and the
 * touched boundary in different places on exactly the devices the cap is for, and that is the worst
 * kind of mismatch: the boundary is only *visible* while the hint is up, so from the second ball
 * onwards the lie would be invisible.
 *
 * The fractions remain the fallback for the frame before the first measurement, where there is no
 * height to take a percentage of and nothing has been drawn to be wrong about yet.
 *
 * **Here rather than in `MachineStage`, because there are two stages** (M11-62). The Builder's test
 * play is the same three rows over the same machine, and importing them from the play route would
 * have pulled the whole of `MachineStage` — and the account chunk it reaches for — into the
 * Builder. They belong beside `DMD_FRACTION` and `flipperBandHeight`, which are the numbers they
 * are made of.
 */
const STAGE_ROWS_UNMEASURED = `${DMD_FRACTION}fr ${PLAYFIELD_FRACTION}fr ${FLIPPER_FRACTION}fr`

export function stageRows(height: number): string {
  if (!(height > 0)) return STAGE_ROWS_UNMEASURED
  const dmd = Math.round(height * DMD_FRACTION)
  const band = flipperBandHeight(height)
  // The playfield takes what is left rather than a third fixed number, so rounding cannot leave a
  // one-pixel line of background between the rows.
  return `${dmd}px 1fr ${band}px`
}

/**
 * The plunger column's width, as a share of the stage and clamped both ways.
 *
 * Wide enough for a knob a thumb can find without looking, narrow enough that the two flipper
 * buttons keep the rest: 78 px of a 390 px phone leaves 156 px each, which is wider than a thumb
 * and far wider than the 15 px chevron it replaces.
 */
export const PLUNGER_LANE_FRACTION = 0.2
export const PLUNGER_LANE_MIN_PX = 64
export const PLUNGER_LANE_MAX_PX = 96

/**
 * How tall the plunger column is in landscape, where there is no cabinet band to sit in.
 *
 * Anchored to the bottom-right corner — where a plunger is on a real cabinet — and given real
 * travel, because the draw distance a player can reach is the whole reason the column exists.
 */
export const PLUNGER_LANE_HEIGHT_FRACTION = 0.55
export const PLUNGER_LANE_MAX_HEIGHT_PX = 220

export function plungerLaneWidth(width: number): number {
  const w = width > 0 ? width : 1
  const wanted = clamp(w * PLUNGER_LANE_FRACTION, PLUNGER_LANE_MIN_PX, PLUNGER_LANE_MAX_PX)
  // …and never more than a third of the stage, so a very narrow viewport still has two flipper
  // buttons rather than a plunger and a rounding error.
  return Math.round(Math.min(wanted, w / 3))
}

export function plungerLaneHeight(height: number): number {
  const h = height > 0 ? height : 1
  return Math.round(Math.min(h * PLUNGER_LANE_HEIGHT_FRACTION, PLUNGER_LANE_MAX_HEIGHT_PX))
}

/**
 * The share of the width each landscape flipper zone takes.
 *
 * A third each, per 08 §5.3, which leaves a third of the middle for the playfield — and the
 * playfield in landscape is a tall rectangle in a wide frame, so the sides were never showing
 * anything anyway. Thumbs land there because that is where a phone held in two hands is gripped.
 */
export const LANDSCAPE_ZONE_FRACTION = 1 / 3

/**
 * Below this aspect ratio the layout is portrait.
 *
 * The ratio rather than an orientation API: `screen.orientation` describes the *device*, and a
 * phone in a browser with a keyboard open, or a tablet in a split view, is a portrait-shaped
 * viewport on a landscape-shaped device. What the layout has to fit is the viewport.
 */
export const PORTRAIT_MAX_ASPECT = 1

export function orientationFor(width: number, height: number): Orientation {
  if (!(width > 0) || !(height > 0)) return 'portrait'
  return width / height <= PORTRAIT_MAX_ASPECT ? 'portrait' : 'landscape'
}

/** Where each band of the portrait stack sits. Landscape has no DMD strip of its own. */
export interface PhoneLayout {
  readonly orientation: Orientation
  /** The DMD strip. Zero-height in landscape, where the backglass is in the 3-D scene. */
  readonly dmd: Rect
  /** The machine. */
  readonly playfield: Rect
  /** Left flipper zone. */
  readonly left: Rect
  /** Right flipper zone. */
  readonly right: Rect
  /**
   * The plunger's column.
   *
   * **Always here, live only while a ball waits in the lane** — see `roleAt`. In portrait it is
   * carved out of the band and touches nothing else; in landscape there is no band to carve, so it
   * sits in the bottom-right corner and overlaps the right zone, which is why `roleAt` still tests
   * it first.
   */
  readonly plunger: Rect
}

export function phoneLayout(width: number, height: number): PhoneLayout {
  const w = width > 0 ? width : 1
  const h = height > 0 ? height : 1
  const orientation = orientationFor(w, h)

  if (orientation === 'landscape') {
    const laneWidth = plungerLaneWidth(w)
    // 08 §5.3. The zones run the full height, because a phone held in two hands has thumbs at
    // the sides from top to bottom and a band at the bottom would be a band under the fingers
    // that are holding it.
    const zone = w * LANDSCAPE_ZONE_FRACTION
    const laneHeight = plungerLaneHeight(h)
    return {
      orientation,
      dmd: { x: 0, y: 0, width: w, height: 0 },
      playfield: { x: 0, y: 0, width: w, height: h },
      left: { x: 0, y: 0, width: zone, height: h },
      right: { x: w - zone, y: 0, width: zone, height: h },
      // The bottom-right corner, which is where a plunger is. It overlaps the right zone and the
      // overlap is settled by `roleAt`; nothing visible may be drawn inside it.
      plunger: { x: w - laneWidth, y: h - laneHeight, width: laneWidth, height: laneHeight },
    }
  }

  const dmdHeight = Math.round(h * DMD_FRACTION)
  const flipperHeight = flipperBandHeight(h)
  const playHeight = Math.max(1, h - dmdHeight - flipperHeight)
  const flipperTop = dmdHeight + playHeight
  const bandHeight = h - flipperTop
  return {
    orientation,
    dmd: { x: 0, y: 0, width: w, height: dmdHeight },
    playfield: { x: 0, y: dmdHeight, width: w, height: playHeight },
    ...bandControls({ x: 0, y: flipperTop, width: w, height: bandHeight }),
  }
}

/**
 * How a cabinet band divides into two flipper buttons and the plunger's column.
 *
 * **The arithmetic, on its own, because there is more than one stage.** `/play` carves a band out
 * of the screen and so, since M11-62, does the Builder's test play. Both have to divide it the same
 * way or the two surfaces are two machines, and a copy of five lines is how that happens.
 *
 * The two buttons share what the plunger's column leaves, and share it evenly: a real cabinet's
 * flipper buttons are one part on each side, and a player who found one has found the other.
 */
function bandControls(band: Rect): Pick<PhoneLayout, 'left' | 'right' | 'plunger'> {
  const laneWidth = plungerLaneWidth(band.width)
  const flipperWidth = band.width - laneWidth
  const half = Math.round(flipperWidth / 2)
  return {
    left: { x: band.x, y: band.y, width: half, height: band.height },
    right: { x: band.x + half, y: band.y, width: flipperWidth - half, height: band.height },
    plunger: {
      x: band.x + flipperWidth,
      y: band.y,
      width: band.width - flipperWidth,
      height: band.height,
    },
  }
}

/** Is this point inside the rectangle? Left and top inclusive, right and bottom exclusive. */
export function inRect(rect: Rect, x: number, y: number): boolean {
  return x >= rect.x && x < rect.x + rect.width && y >= rect.y && y < rect.y + rect.height
}

/** Which control a touch that started at this point is operating. */
export type TouchRole = 'leftFlipper' | 'rightFlipper' | 'plunger' | 'playfield' | 'none'

/**
 * What a touch starting here does.
 *
 * Decided **once, on touch-down**, and held for the life of that touch. A finger that starts on
 * the left flipper and slides into the right zone is still holding the left flipper: a player
 * rolling a thumb along the bottom of the screen is not asking to swap hands, and a role that
 * changed under a moving finger would make a held flipper release itself mid-shot.
 *
 * `plungerLive` is whether there is a ball in the shooter lane. **The column is still there when
 * it is false** — it is the plunger's housing and the knob is drawn seated in it — but a touch in
 * it does nothing, which is what a plunger with no ball behind it does.
 */
export function roleAt(
  layout: PhoneLayout,
  plungerLive: boolean,
  x: number,
  y: number,
): TouchRole {
  // First, because in landscape the column overlaps the right zone and there is no band to carve
  // it out of. In portrait it overlaps nothing and the order is merely harmless.
  if (plungerLive && inRect(layout.plunger, x, y)) return 'plunger'
  if (inRect(layout.left, x, y)) return 'leftFlipper'
  if (inRect(layout.right, x, y)) return 'rightFlipper'
  // The column when it is not live: dead, and deliberately not a flipper. A thumb that finds the
  // plunger and gets a flipper has been told the wrong thing about where the controls are.
  if (inRect(layout.plunger, x, y)) return 'none'
  if (inRect(layout.playfield, x, y)) return 'playfield'
  return 'none'
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}
